import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import Link from '@mui/material/Link';
import { useTranslation } from 'react-i18next';
import type { LoanResponseConfigParametersNames } from '@repo/api/endpoints/merchant';
import { colorPalette } from '@repo/mui-config/color-palette';
import { Button, ButtonLink, Select } from '@repo/ui';
import type { OptionsProps } from '@repo/ui';

export interface DetailType {
  text?: {
    name: LoanResponseConfigParametersNames;
    value: string | number;
  };
  select?: {
    name: LoanResponseConfigParametersNames;
    options: OptionsProps[];
    defaultValue?: OptionsProps['value'];
  };
  button?: {
    name:
      | Extract<LoanResponseConfigParametersNames, 'phone' | 'mail' | 'documentId'>
      | 'documentType';
    onClick: () => void;
  };
  link?: {
    isEdit?: boolean;
    name:
      | Extract<LoanResponseConfigParametersNames, 'phone' | 'mail' | 'documentId'>
      | 'documentType';
    href: string;
  };
}

interface RenderDetailByTypeProps {
  detailType: DetailType;
  sx?: {
    marginBottom: string | number;
  };
}

export function RenderDetailByType({ detailType, ...rest }: RenderDetailByTypeProps) {
  const { t } = useTranslation();

  if (detailType.text) {
    return (
      <Typography
        color={colorPalette.ink.light}
        data-testid={`detail-field-${detailType.text.name}`}
        variant="textField"
        {...rest}
      >
        {detailType.text.value}
      </Typography>
    );
  }

  if (detailType.select) {
    return (
      <Select
        defaultValue={detailType.select.defaultValue}
        name={detailType.select.name}
        options={detailType.select.options}
        {...rest}
      />
    );
  }

  if (detailType.button) {
    return (
      <Button
        color="info"
        onClick={detailType.button.onClick}
        size="small"
        variant="contained"
        {...rest}
      >
        <Stack direction="row" gap={1}>
          {t(`loanFormPage.borrowerDetails.form.addFieldValues.${detailType.button.name}`)}
          <ArrowForward
            sx={{
              fontSize: '18px',
              marginTop: '2px',
            }}
          />
        </Stack>
      </Button>
    );
  }

  if (detailType.link) {
    return (
      <ButtonLink
        color="info"
        component={Link}
        href={detailType.link.href}
        size="small"
        variant="contained"
        target="_top"
        {...rest}
      >
        <Stack direction="row" gap={1}>
          {t(
            `loanFormPage.borrowerDetails.form.${
              detailType.link.isEdit ? 'editFieldValues' : 'addFieldValues'
            }.${detailType.link.name}`,
          )}
          <ArrowForward
            sx={{
              fontSize: '18px',
              marginTop: '2px',
            }}
          />
        </Stack>
      </ButtonLink>
    );
  }

  return null;
}
